import { useState } from 'react';

import PropTypes from 'prop-types';

import { FormattedMessage } from '@zenbusiness/application-commons-intl';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Checkbox from '@zenbusiness/zui/Checkbox';
import FormControlLabel from '@zenbusiness/zui/FormControlLabel';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import SubscriptionSummaryContent from './SubscriptionSummaryContent';

import {
  StyledSummaryCard,
  StyledSummaryRow,
  StyledSummaryDivider,
  StyledContinueButtonRow,
} from '../SubscriptionCancelPage.style';
import {
  CANCEL_FLOW_TYPES,
  getCancelClickstreamProps,
  getCancelMessageId,
  subscriptionContextPropType,
} from '../utils/cancelFlowConfig';

function SubscriptionCancelConfirmation({
  cancelFlowType,
  subscription,
  subscriptionContext,
  changeReasonDisplay,
  onChangeReason,
  onSubmit,
}) {
  const [termsAgreed, setTermsAgreed] = useState(false);

  return (
    <>
      <Stack gap={1.5}>
        <Text size="2xl" weight="bold" family="secondary">
          <FormattedMessage id="dashboard.subscriptionCancel.confirmation.heading" />
        </Text>
        <Text size="md" color="secondary">
          <FormattedMessage id={getCancelMessageId(cancelFlowType, 'confirmationSubheading')} />
        </Text>
      </Stack>

      <Stack gap={3}>
        <StyledSummaryCard>
          <SubscriptionSummaryContent
            period={subscription.period}
            summary={subscription.summary}
            service={subscription.service}
            titleMessageId={subscription.titleMessageId}
            price={subscription.price}
            summaryDate={subscription.summaryDate}
            inTrial={subscription.inTrial}
            trialDays={subscription.trialDays}
            cancelFlowType={cancelFlowType}
            variant="summary"
          />

          <StyledSummaryDivider />

          <StyledSummaryRow>
            <Box>
              <Text size="xs" color="secondary">
                <FormattedMessage id="dashboard.subscriptionCancel.confirmation.reason.label" />
              </Text>
              <Text size="sm" weight="bold">
                {changeReasonDisplay}
              </Text>
            </Box>
            <Box flex={1} textAlign="right">
              <Button
                variant="text"
                size="small"
                clickstreamProps={getCancelClickstreamProps(cancelFlowType, 'changeReason', subscriptionContext)}
                onClick={onChangeReason}
                sx={{
                  fontWeight: 'bold',
                  color: 'primary.main',
                  minWidth: 'auto',
                  padding: 0,
                }}
              >
                <FormattedMessage id="dashboard.subscriptionCancel.confirmation.reason.change" />
              </Button>
            </Box>
          </StyledSummaryRow>
        </StyledSummaryCard>

        <Box>
          <FormControlLabel
            control={<Checkbox checked={termsAgreed} onChange={(e) => setTermsAgreed(e.target.checked)} />}
            label={<FormattedMessage id="dashboard.subscriptionCancel.confirmation.terms.checkboxLabel" />}
          />
          <Box pl={5}>
            <Text size="xs" color="tertiary">
              <FormattedMessage id="dashboard.subscriptionCancel.confirmation.terms.disclaimer" />
            </Text>
          </Box>
        </Box>
      </Stack>

      <StyledContinueButtonRow>
        <Button
          variant="contained"
          size="large"
          disabled={!termsAgreed}
          clickstreamProps={getCancelClickstreamProps(cancelFlowType, 'submit', subscriptionContext)}
          onClick={onSubmit}
          sx={{
            borderRadius: '12px',
            height: 56,
            ...(termsAgreed && {
              backgroundColor: 'error.main',
              '&:hover': {
                backgroundColor: 'error.dark',
              },
            }),
          }}
        >
          <FormattedMessage id={getCancelMessageId(cancelFlowType, 'confirmationSubmit')} />
        </Button>
      </StyledContinueButtonRow>
    </>
  );
}

SubscriptionCancelConfirmation.propTypes = {
  cancelFlowType: PropTypes.oneOf(Object.values(CANCEL_FLOW_TYPES)).isRequired,
  subscription: PropTypes.shape({
    service: PropTypes.string,
    summary: PropTypes.string,
    titleMessageId: PropTypes.string,
    price: PropTypes.number,
    period: PropTypes.string,
    inTrial: PropTypes.bool,
    trialDays: PropTypes.number,
    summaryDate: PropTypes.string,
  }).isRequired,
  subscriptionContext: subscriptionContextPropType,
  changeReasonDisplay: PropTypes.string.isRequired,
  onChangeReason: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default SubscriptionCancelConfirmation;
